(() => {
  const selectors = {
    section: '[data-ab-showroom-section]',
    mainImage: '[data-ab-showroom-main-image]',
    thumb: '[data-ab-showroom-thumb]',
    prev: '[data-ab-showroom-prev]',
    next: '[data-ab-showroom-next]',
    option: '[data-ab-showroom-option]',
    variantInput: '[data-ab-showroom-variant-id]',
    price: '[data-ab-showroom-price]',
    compare: '[data-ab-showroom-compare]',
    quantity: '[data-ab-showroom-quantity]',
    quantityMinus: '[data-ab-showroom-quantity-minus]',
    quantityPlus: '[data-ab-showroom-quantity-plus]',
    shipping: '[data-ab-showroom-shipping]',
    form: '[data-ab-showroom-form]',
    add: '[data-ab-showroom-add]',
    buy: '[data-ab-showroom-buy]',
    variants: '[data-ab-showroom-variants]',
  };

  const formatMoney = (cents, currency) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
    } catch (error) {
      return `$${value.toFixed(2)}`;
    }
  };

  const activeImageIndex = (section) => {
    const active = section.querySelector(`${selectors.thumb}[aria-current="true"]`);
    return Number(active?.dataset.abShowroomIndex || 0);
  };

  const preloadGalleryImages = (section) => {
    const main = section.querySelector(selectors.mainImage);
    const thumbs = [...section.querySelectorAll(selectors.thumb)];
    const seen = new Set();

    thumbs.forEach((thumb, index) => {
      const src = thumb.dataset.abShowroomFull;
      const srcset = thumb.dataset.abShowroomSrcset;
      const key = srcset || src;
      if (!key || seen.has(key)) return;
      seen.add(key);

      const image = new Image();
      image.decoding = "async";
      image.loading = "eager";
      if (index <= 2) image.fetchPriority = "high";
      if (main?.sizes) image.sizes = main.sizes;
      if (srcset) image.srcset = srcset;
      if (src) image.src = src;
    });
  };

  const setImage = (section, index) => {
    const images = [...section.querySelectorAll(selectors.thumb)];
    const main = section.querySelector(selectors.mainImage);
    if (!main || images.length === 0) return;
    const nextIndex = (index + images.length) % images.length;
    const next = images[nextIndex];
    const nextSrc = next.dataset.abShowroomFull;
    const nextSrcset = next.dataset.abShowroomSrcset;
    const nextAlt = next.dataset.abShowroomAlt || main.alt;
    const previewImage = next.querySelector('img');
    const previewSrc = previewImage?.currentSrc || previewImage?.src;

    const applyFullImage = () => {
      if (nextSrcset) main.srcset = nextSrcset;
      else main.removeAttribute('srcset');
      if (nextSrc) main.src = nextSrc;
      main.alt = nextAlt;
    };

    if (previewSrc && previewSrc !== (main.currentSrc || main.src)) {
      main.removeAttribute('srcset');
      main.src = previewSrc;
      main.alt = nextAlt;
      requestAnimationFrame(applyFullImage);
    } else {
      applyFullImage();
    }

    images.forEach((thumb, thumbIndex) => {
      thumb.setAttribute('aria-current', String(thumbIndex === nextIndex));
    });
  };

  const selectedOptions = (section) => [...section.querySelectorAll(selectors.option)].map((select) => select.value);

  const updateVariant = (section) => {
    const script = section.querySelector(selectors.variants);
    if (!script) return;

    let variants = [];
    try {
      variants = JSON.parse(script.textContent || '[]');
    } catch (error) {
      variants = [];
    }

    const options = selectedOptions(section);
    const variant =
      options.length === 0
        ? variants[0]
        : variants.find((item) => item.options.every((option, index) => option === options[index]));
    const input = section.querySelector(selectors.variantInput);
    const price = section.querySelector(selectors.price);
    const compare = section.querySelector(selectors.compare);
    const add = section.querySelector(selectors.add);
    const buy = section.querySelector(selectors.buy);
    const currency = section.dataset.currency || 'USD';

    if (!variant) {
      if (add) add.disabled = true;
      if (buy) buy.disabled = true;
      return;
    }

    if (input) input.value = variant.id;
    if (price) price.textContent = formatMoney(variant.price, currency);
    if (compare) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compare.textContent = formatMoney(variant.compare_at_price, currency);
        compare.hidden = false;
      } else {
        compare.hidden = true;
      }
    }

    const disabled = !variant.available;
    if (add) add.disabled = disabled;
    if (buy) buy.disabled = disabled;
  };

  const setBusy = (button, busy, text) => {
    if (!button) return;
    if (busy) {
      button.dataset.originalText = button.textContent;
      button.textContent = text || 'Working...';
      button.disabled = true;
      return;
    }
    button.disabled = false;
    if (button.dataset.originalText) button.textContent = button.dataset.originalText;
  };

  const addToCart = async (section, checkout = false) => {
    const form = section.querySelector(selectors.form);
    const button = section.querySelector(checkout ? selectors.buy : selectors.add);
    if (!form || !button) return;

    setBusy(button, true, checkout ? 'Opening...' : 'Adding...');
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!response.ok) throw new Error('Unable to add item');
      if (checkout) {
        window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
        return;
      }
      button.textContent = form.dataset.addedText || 'Added';
      setTimeout(() => setBusy(button, false), 1500);
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      setBusy(button, false);
      form.submit();
    }
  };

  const initSection = (section) => {
    section.addEventListener('click', (event) => {
      const thumb = event.target.closest(selectors.thumb);
      if (thumb && section.contains(thumb)) {
        setImage(section, Number(thumb.dataset.abShowroomIndex || 0));
      }

      const prev = event.target.closest(selectors.prev);
      if (prev && section.contains(prev)) setImage(section, activeImageIndex(section) - 1);

      const next = event.target.closest(selectors.next);
      if (next && section.contains(next)) setImage(section, activeImageIndex(section) + 1);

      const shipping = event.target.closest(selectors.shipping);
      if (shipping && section.contains(shipping)) {
        section.querySelectorAll(selectors.shipping).forEach((item) => item.classList.remove('is-active'));
        shipping.classList.add('is-active');
      }

      const minus = event.target.closest(selectors.quantityMinus);
      const plus = event.target.closest(selectors.quantityPlus);
      if (minus || plus) {
        const input = section.querySelector(selectors.quantity);
        if (!input) return;
        const current = Math.max(1, Number.parseInt(input.value || '1', 10));
        input.value = String(Math.max(1, current + (plus ? 1 : -1)));
      }

      const buy = event.target.closest(selectors.buy);
      if (buy && section.contains(buy)) {
        event.preventDefault();
        addToCart(section, true);
      }
    });

    section.addEventListener('change', (event) => {
      if (event.target.matches(selectors.option)) updateVariant(section);
    });

    const form = section.querySelector(selectors.form);
    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      addToCart(section, false);
    });

    setImage(section, 0);
    preloadGalleryImages(section);
    updateVariant(section);
  };

  const init = (root = document) => {
    root.querySelectorAll(selectors.section).forEach((section) => {
      if (section.dataset.abShowroomInitialized === 'true') return;
      section.dataset.abShowroomInitialized = 'true';
      initSection(section);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
